import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import type { SvgIconComponent } from "@mui/icons-material";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import type { ReactNode } from "react";

/**
 * The proportions of a printed business card (3.5in by 2in), at the calendar hover card's width, so
 * the card reads as one object rather than a menu. A minimum rather than a fixed height: a long firm
 * name or two lines of credentials grow the card instead of being cut off.
 */
const CARD_WIDTH = 320;
const CARD_MIN_HEIGHT = Math.round((CARD_WIDTH * 2) / 3.5);

const Card = styled("div")(({ theme }) => ({
  borderTop: `4px solid ${theme.palette.primary.main}`,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
  maxWidth: `calc(100vw - ${theme.spacing(2)})`,
  minHeight: CARD_MIN_HEIGHT,
  padding: theme.spacing(1.5, 2),
  width: CARD_WIDTH,
}));
const Letterhead = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  lineHeight: 1.6,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const Identity = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1.5),
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
}));
const CardAvatar = styled(IndividualAvatar)(({ theme }) => ({
  height: theme.spacing(6),
  width: theme.spacing(6),
}));
// A div, not subtitle1's default h6: the card is transient and should not add to the page's outline.
const Name = styled("div")(({ theme }) => ({
  ...theme.typography.subtitle1,
  fontWeight: 600,
  lineHeight: 1.3,
  overflowWrap: "anywhere",
}));
const Credentials = styled(Typography)(() => ({
  display: "-webkit-box",
  overflow: "hidden",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
/** How to reach the person, along the bottom edge as on a printed card. */
const ContactLines = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  marginTop: "auto",
}));
const ContactLine = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "grid",
  gridTemplateColumns: "18px minmax(0, 1fr)",
  "& svg": { color: theme.palette.action.active, fontSize: 18 },
}));
const ContactText = styled(Typography)(() => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type IndividualBusinessCardProps = {
  individual: Individual;
  /** The id on the person's name, for the surface around the card to be labelled by. */
  titleId: string;
  /** What runs along the bottom edge under the contact lines, such as a link to the profile. */
  footer?: ReactNode;
};

/**
 * A person as a business card: their firm across the top, who they are in the middle, and how to
 * reach them along the bottom.
 *
 * The face alone. The click-opened profile popover wraps it in a link to the profile (#5811); the
 * hover card shows it as it is, because a card that opens under a passing pointer must not also be a
 * place to click (#5813).
 */
export default function IndividualBusinessCard(props: Readonly<IndividualBusinessCardProps>) {
  const { individual, titleId, footer } = props;
  const email = individual.email?.value;
  const phone = individual.profile.phoneNumber?.value;

  return (
    <Card>
      {individual.entityName && <Letterhead variant="overline">{individual.entityName}</Letterhead>}
      <Identity>
        <CardAvatar individual={individual} size="medium" />
        <div>
          <Name id={titleId}>{individual.fullName}</Name>
          {individual.profile.credentials && (
            <Credentials variant="body2" color="text.secondary">
              {individual.profile.credentials}
            </Credentials>
          )}
        </div>
      </Identity>
      {(email || phone || footer) && (
        <ContactLines>
          {(email || phone) && <Divider />}
          {email && <CardContactLine icon={EmailOutlinedIcon} label="Email">{email}</CardContactLine>}
          {phone && <CardContactLine icon={PhoneOutlinedIcon} label="Phone">{phone}</CardContactLine>}
          {footer}
        </ContactLines>
      )}
    </Card>
  );
}

function CardContactLine(props: Readonly<{ icon: SvgIconComponent; label: string; children: ReactNode }>) {
  const { icon: Icon, label, children } = props;
  return (
    <ContactLine>
      <Icon aria-label={label} />
      <ContactText variant="body2">{children}</ContactText>
    </ContactLine>
  );
}
